import { User, Trash2, Pencil, Plus } from 'lucide-react'
import { Button } from 'antd'
import type { StakeholderTemplate } from '@bandos/shared'
import { DismissibleTooltip } from '../../../../common/DismissibleTooltip'
import { ProfileView } from './ProfileView'
import { StakeholderForm } from './StakeholderForm'
import type { RightPanelView, FormData, ArrayField } from '../CharacterSelectionModal.types'
import { MAX_TEAM_SIZE } from '../CharacterSelectionModal.types'
import {
  ProfilePanel as StyledProfilePanel,
  ProfileHeader,
  ProfileEmptyState,
  ProfileEmptyIcon,
  ProfileEmptyText,
  ProfileActionBar,
} from '../styles/CharacterSelectionModalStyles'

interface ProfilePanelProps {
  rightPanelView: RightPanelView
  displayTemplate: StakeholderTemplate | null
  editingTemplate: StakeholderTemplate | null
  formData: FormData
  submitting: boolean
  teamSize: number
  operatingIds: Set<string>
  brokenPortraits: Set<string>
  markPortraitBroken: (id: string) => void
  isTemplateInTeam: (id: string) => boolean
  isGeneratingPortrait: (id: string) => boolean
  updateFormField: (field: keyof FormData, value: string | string[]) => void
  addArrayItem: (field: ArrayField) => void
  removeArrayItem: (field: ArrayField, index: number) => void
  updateArrayItem: (field: ArrayField, index: number, value: string) => void
  onEditClick: (template: StakeholderTemplate, e: React.MouseEvent) => void
  onAddToTeam: (template: StakeholderTemplate, e: React.MouseEvent) => void
  onDeleteTemplate: () => void
  onFormCancel: () => void
  onCreateSubmit: () => void
  onEditSave: () => void
  onSaveAsNew: () => void
}

export function ProfilePanel({
  rightPanelView,
  displayTemplate,
  editingTemplate,
  formData,
  submitting,
  teamSize,
  operatingIds,
  brokenPortraits,
  markPortraitBroken,
  isTemplateInTeam,
  isGeneratingPortrait,
  updateFormField,
  addArrayItem,
  removeArrayItem,
  updateArrayItem,
  onEditClick,
  onAddToTeam,
  onDeleteTemplate,
  onFormCancel,
  onCreateSubmit,
  onEditSave,
  onSaveAsNew,
}: ProfilePanelProps) {
  return (
    <StyledProfilePanel>
      {(rightPanelView === 'empty' || rightPanelView === 'profile') && (
        <ProfileHeader
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <span>Profile</span>
          {rightPanelView === 'profile' && displayTemplate && (
            <ProfileActionBar
              style={{ margin: 0, padding: 0, position: 'static', background: 'transparent' }}
            >
              <DismissibleTooltip
                title={displayTemplate.is_public ? 'Public templates cannot be deleted' : 'Delete'}
              >
                <Button
                  type="text"
                  shape="circle"
                  danger
                  icon={<Trash2 size={16} />}
                  disabled={displayTemplate.is_public || submitting}
                  onClick={onDeleteTemplate}
                />
              </DismissibleTooltip>
              <DismissibleTooltip title="Edit">
                <Button
                  type="text"
                  shape="circle"
                  icon={<Pencil size={16} />}
                  onClick={(e) => onEditClick(displayTemplate, e)}
                />
              </DismissibleTooltip>
              {!isTemplateInTeam(displayTemplate.id) && teamSize < MAX_TEAM_SIZE && (
                <DismissibleTooltip title="Add to Team">
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<Plus size={16} />}
                    disabled={operatingIds.has(displayTemplate.id)}
                    onClick={(e) => void onAddToTeam(displayTemplate, e)}
                  />
                </DismissibleTooltip>
              )}
            </ProfileActionBar>
          )}
        </ProfileHeader>
      )}

      {rightPanelView === 'empty' && (
        <ProfileEmptyState>
          <ProfileEmptyIcon>
            <User size={28} />
          </ProfileEmptyIcon>
          <ProfileEmptyText>Select a team member to view their profile</ProfileEmptyText>
        </ProfileEmptyState>
      )}

      {rightPanelView === 'profile' && displayTemplate && (
        <ProfileView
          template={displayTemplate}
          isGenerating={isGeneratingPortrait(displayTemplate.id)}
          brokenPortraits={brokenPortraits}
          markPortraitBroken={markPortraitBroken}
        />
      )}

      {rightPanelView === 'create' && (
        <StakeholderForm
          mode="create"
          formData={formData}
          editingTemplate={null}
          submitting={submitting}
          updateFormField={updateFormField}
          addArrayItem={addArrayItem}
          removeArrayItem={removeArrayItem}
          updateArrayItem={updateArrayItem}
          onCancel={onFormCancel}
          onCreateSubmit={onCreateSubmit}
          onEditSave={onEditSave}
          onSaveAsNew={onSaveAsNew}
        />
      )}

      {rightPanelView === 'edit' && (
        <StakeholderForm
          mode="edit"
          formData={formData}
          editingTemplate={editingTemplate}
          submitting={submitting}
          updateFormField={updateFormField}
          addArrayItem={addArrayItem}
          removeArrayItem={removeArrayItem}
          updateArrayItem={updateArrayItem}
          onCancel={onFormCancel}
          onCreateSubmit={onCreateSubmit}
          onEditSave={onEditSave}
          onSaveAsNew={onSaveAsNew}
        />
      )}
    </StyledProfilePanel>
  )
}
